import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { filter } from 'rxjs/operators';

import { Capacitor } from '@capacitor/core';
import { Analytics } from 'src/providers/analytics/analytics';
import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { AfterLoginRedirect } from 'src/providers/auth/afterLoginRedirect';
import { Events } from 'src/providers/events/events';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Util } from 'src/shared/util/util';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { validateEmail } from 'src/shared/validator/validateEmail';
import { CONNECTION_ERROR_MESSAGE, isConnectionError } from 'src/shared/util/connection-error';
import { SocialAuthService, SocialAuthResult } from 'src/shared/services/social-auth.service';
import { GuestAccountService } from 'src/shared/services/guest-account.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { RecoverAccountService } from 'src/providers/recover-account/recover-account';
import { propertyDashboardPath } from 'src/providers/guard/rental-routes.guard';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { DemoAccountService, DEMO_UNAVAILABLE_MESSAGE } from 'src/shared/services/demo-account.service';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { PosthogService } from 'src/shared/services/posthog.service';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';
import { Subscription } from 'rxjs';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { BookService } from 'src/shared/services/book.service';

type AuthMode = 'signin' | 'signup';
type AccountType = 'consumer' | 'rental' | 'company' | 'pro';
type GuestType = 'rental' | 'consumer' | 'pro' | 'company';
type ModalMode = 'auth' | 'convert' | 'guest-type';
type Step = 'auth' | 'account-type' | 'convert' | 'guest-type' | 'reset-password' | 'forgot-username' | 'phone-login';

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.scss'],
  standalone: false
})
export class AuthModalComponent implements OnInit, OnDestroy {
  @Input() alertMessage = '';
  @Input() initialMode: ModalMode = 'auth';
  @Input() defaultTab: AuthMode = 'signup';
  /** Sign-in-only entry (e.g. from the demo account): hides the Sign Up tab. */
  @Input() signInOnly = false;
  mode: AuthMode = 'signup';
  step: Step = 'auth';
  signInForm: UntypedFormGroup;
  signUpForm: UntypedFormGroup;
  convertForm: UntypedFormGroup;
  resetPasswordForm: UntypedFormGroup;
  forgotUsernameForm: UntypedFormGroup;
  phoneLoginForm: UntypedFormGroup;
  codeSent = false;
  isLoading = false;
  isTokenLoginLoading = false;
  errorMessage = '';
  successMessage = '';
  submitted = false;
  selectedAccountType: AccountType | null = null;
  selectedGuestType: GuestType | null = null;
  loadingGuestType: string | null = null;
  private translationPipe = new TranslationPipe();

  get guestTypes() {
    return [
      { value: 'rental', label: this.translationPipe.transform('For Property Managers / Hosts'), icon: 'apartment', description: this.translationPipe.transform('Manage short-term rentals, vacation properties, or multiple units') },
      { value: 'regular', label: this.translationPipe.transform('For Your Home'), icon: 'home', description: this.translationPipe.transform('Keep your personal home clean and maintained') },
      { value: 'pro', label: this.translationPipe.transform('For Service Pros'), icon: 'handyman', description: this.translationPipe.transform('I provide cleaning or maintenance services') },
      { value: 'company', label: this.translationPipe.transform('For the Office'), icon: 'business', description: this.translationPipe.transform('Keep your office or commercial space clean and maintained') }
    ];
  }

  get accountTypes() {
    return [
      { value: 'regular', label: this.translationPipe.transform('Consumer'), icon: 'home', description: this.translationPipe.transform('I want to book services for my home') },
      { value: 'rental', label: this.translationPipe.transform('Rental'), icon: 'apartment', description: this.translationPipe.transform('I manage short-term rentals or vacation properties') },
      { value: 'company', label: this.translationPipe.transform('Company'), icon: 'business', description: this.translationPipe.transform('I manage properties for a business or organization') },
      { value: 'pro', label: this.translationPipe.transform('Service Provider'), icon: 'handyman', description: this.translationPipe.transform('I provide cleaning or maintenance services') }
    ];
  }
  isLoggedIn = false;
  private tokenLoginSubscription: Subscription;
  private routerSubscription: Subscription;
  platform = Capacitor.getPlatform();

  constructor(
    private afterLoginRedirect: AfterLoginRedirect,
    private addressesQueries: AddressesQueriesService,
    private analytics: Analytics,
    private auth: Auth,
    private client: Client,
    private events: Events,
    private fb: UntypedFormBuilder,
    private guestAccountService: GuestAccountService,
    private me: Me,
    private modalController: ModalController,
    private navCtrl: CustomNavController,
    private route: ActivatedRoute,
    private router: Router,
    private socialAuth: SocialAuthService,
    private storage: TidyStorage,
    private util: Util,
    private onboardingProvider: OnboardingProvider,
    private recoverAccountService: RecoverAccountService,
    private authModalService: AuthModalService,
    private demoAccountService: DemoAccountService,
    private posthogService: PosthogService,
    private pmDemoLanding: PmDemoLandingService,
    private bookService: BookService
  ) {
    this.signInForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, validateEmail])],
      password: ['', Validators.required]
    });
    this.signUpForm = this.fb.group({
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      email: ['', Validators.compose([Validators.required, validateEmail])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(8)])]
    });
    this.convertForm = this.fb.group({
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      email: ['', Validators.compose([Validators.required, validateEmail])],
      password: ['', Validators.compose([Validators.required, Validators.minLength(8)])]
    });
    this.resetPasswordForm = this.fb.group({
      email: ['', Validators.compose([Validators.required, validateEmail])]
    });
    this.forgotUsernameForm = this.fb.group({
      phone: ['', Validators.required],
      address1: ['', Validators.required],
      zipcode: ['', Validators.required]
    });
    this.phoneLoginForm = this.fb.group({
      phone: ['', Validators.required],
      code: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.isLoggedIn = this.auth.isAuthenticated();
    this.mode = this.signInOnly ? 'signin' : this.defaultTab;
    if (this.initialMode === 'convert') {
      this.step = 'convert';
    } else if (this.initialMode === 'guest-type') {
      this.step = 'guest-type';
      const savedGuestType = localStorage.getItem('guest_view_type');
      if (savedGuestType) {
        this.selectedGuestType = savedGuestType as GuestType;
      }
      // Warm the pm-demo landing variant now (no exposure recorded) so that picking
      // "For Property Managers / Hosts" navigates instantly without waiting on PostHog.
      // Enrollment still happens only on that click (resolveLandingRoute fires exposure).
      this.pmDemoLanding.prefetchVariant();
    } else if (this.initialMode === 'auth') {
      this.step = 'auth';
    }
    if (this.demoAccountService.isDemoSession()) {
      const pendingSignup = this.demoAccountService.getPendingSignup();
      if (pendingSignup) {
        this.signUpForm.patchValue(pendingSignup);
      }
    }
    this.tokenLoginSubscription = this.authModalService.tokenLoginLoading$.subscribe(loading => {
      this.isTokenLoginLoading = loading;
    });
    this.checkForTokenInUrl();
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkForTokenInUrl();
      });
  }

  checkForTokenInUrl() {
    setTimeout(() => {
      const currentUrl = this.router.url;
      if (currentUrl.includes('/login/')) {
        const loginTokenMatch = currentUrl.match(/\/login\/([^\/\?]+)/);
        if (loginTokenMatch && loginTokenMatch[1]) {
          this.isTokenLoginLoading = true;
          this.authModalService.setTokenLoginLoading(true);
        }
      }
    }, 100);
  }

  ngOnDestroy() {
    if (this.tokenLoginSubscription) {
      this.tokenLoginSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  dismiss() {
    this.modalController.dismiss();
  }

  switchMode(mode: AuthMode) {
    this.mode = mode;
    this.errorMessage = '';
    this.submitted = false;
  }

  async signInWithGoogle() {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const authResult = await this.socialAuth.signInWithGoogle();
      await this.handleSocialAuthSuccess(authResult);
    } catch (error: any) {
      this.handleSocialAuthError(error, 'Google');
    } finally {
      this.isLoading = false;
    }
  }

  async signInWithApple() {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const authResult = await this.socialAuth.signInWithApple();
      await this.handleSocialAuthSuccess(authResult);
    } catch (error: any) {
      this.handleSocialAuthError(error, 'Apple');
    } finally {
      this.isLoading = false;
    }
  }

  private async handleSocialAuthSuccess(authResult: SocialAuthResult) {
    try {
      const wasGuestAccount = await this.storage.retrieve('isGuestAccount');
      const response = await this.socialAuth.authenticateWithBackend(authResult);
      if (response.is_new_user) {
        // Clear storage only after all operations complete successfully
        this.clearLocalStoragePreservingPosthog();
        await this.storage.clear();
        localStorage.setItem('tidy_token', response.access_token);
        localStorage.setItem('tidy_token_type', response.token_type);
        this.auth.authenticated.set(true);
        await this.storage.save('didJustSignIn', true);
        this.analytics.trackUpfluenceSignup({
          customerId: response.customer_id,
          firstName: authResult.firstName || '',
          lastName: authResult.lastName || '',
          email: authResult.email || '',
          accountType: 'social'
        });
        this.dismiss();
        this.navCtrl.navigateForward('add-first-property/normal');
        if (wasGuestAccount) {
          window.location.reload();
        }
      } else {
        // Clear storage only after all operations complete successfully
        this.clearLocalStoragePreservingPosthog();
        await this.storage.clear();
        localStorage.setItem('tidy_token', response.access_token);
        localStorage.setItem('tidy_token_type', response.token_type);
        await this.storage.save('didJustSignIn', true);
        await this.loadAccountAndRedirect();
        if (wasGuestAccount) {
          window.location.reload();
        }
      }
    } catch (err: any) {
      this.isLoading = false;
      this.errorMessage = 'Authentication failed. Please try again.';
      console.error('handleSocialAuthSuccess error:', err);
    }
  }

  private handleSocialAuthError(error: any, provider: string) {
    this.isLoading = false;
    const errorMessage = error?.message || error?.error?.message || '';
    if (errorMessage.includes('canceled') || errorMessage.includes('cancelled') || errorMessage.includes('popup_closed')) {
      return;
    }
    if (error?.status === 409) {
      this.errorMessage = 'An account with this email already exists. Please sign in with your password.';
    } else if (error?.status === 404) {
      this.errorMessage = 'No account found. Please sign up first.';
    } else if (error?.error?.has_homekeeper_account) {
      this.errorMessage = 'This account is for service providers. Please use the TIDY for Pros app.';
    } else {
      this.errorMessage = `${provider} sign-in failed. Please try again.`;
    }
    console.log('DEBUG: Error message:', error);
  }

  async onSignIn() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.signInForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const wasGuestAccount = await this.storage.retrieve('isGuestAccount');
      const credentials = {
        username: this.signInForm.value.email,
        password: this.signInForm.value.password
      };
      await this.auth.login(credentials, 'login', null);
      await this.completeCustomerLogin(wasGuestAccount);
    } catch (err: any) {
      this.isLoading = false;
      if (isConnectionError(err)) {
        this.errorMessage = CONNECTION_ERROR_MESSAGE;
      } else if (err?.err?.has_homekeeper_account) {
        this.errorMessage = 'This account is for service providers. Please use the TIDY for Pros app.';
      } else {
        this.errorMessage = 'Invalid email or password. Please try again.';
      }
    }
  }

  // Shared post-login sequence for password and phone-code sign-in. Runs only after
  // auth.login has succeeded, so guest state is preserved on failure.
  private async completeCustomerLogin(wasGuestAccount: any): Promise<void> {
    // Preserve tokens before clearing storage
    const token = localStorage.getItem('tidy_token');
    const tokenType = localStorage.getItem('tidy_token_type');
    // auth.login already flipped `authenticated` to true. The TanStack queries gated by that
    // signal (subscriptions, workflows, concierge) would fire inside the clear window below,
    // go out without a bearer, get a 401 and trigger the global logout-on-401. Hide the session
    // until the token is back in storage. Social login avoids this by only setting the signal
    // after the token is restored.
    this.auth.authenticated.set(false);
    // Clear storage only after login succeeds to preserve guest state on failure
    this.clearLocalStoragePreservingPosthog();
    await this.storage.clear();
    // Re-set tokens after clearing
    localStorage.setItem('tidy_token', token);
    localStorage.setItem('tidy_token_type', tokenType);
    this.auth.authenticated.set(true);
    await this.storage.save('didJustSignIn', true);
    const account = await this.me.load(true);
    if (!this.isLoggedIn) {
      await this.onboardingProvider.getGoalsFromServer();
      this.events.publish('goals:updated');
    }
    await this.storage.save('isGuestAccount', false);
    await this.loadAccountAndRedirect(account);
    if (wasGuestAccount) {
      window.location.reload();
    }
  }

  async onSignUp() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.signUpForm.valid) {
      return;
    }
    const isGuestAccount = await this.storage.retrieve('isGuestAccount');
    if (isGuestAccount) {
      await this.completeSignUp();
    } else {
      this.step = 'account-type';
    }
  }

  selectAccountType(type: AccountType) {
    this.selectedAccountType = type;
  }

  selectGuestType(type: GuestType) {
    this.selectedGuestType = type;
  }

  async selectAndConfirmGuestType(type: GuestType) {
    this.selectedGuestType = type;
    this.loadingGuestType = type;
    if (type === 'pro') {
      this.util.openUrl('https://www.tidy.com/pros');
      this.loadingGuestType = null;
      return;
    }
    this.isLoading = true;
    try {
      if (type === 'rental') {
        // The entrance cover doubles as the loading state while we log into the demo
        this.demoAccountService.beginEntrance();
        localStorage.setItem('isRentalClient', 'true');
        localStorage.setItem('guest_view_type', type);
        this.events.publish('auth:type-selected');
        await this.demoAccountService.loginToDemoAccount(type);
        this.modalController.dismiss({ guestType: type });
        this.demoAccountService.revealEntrance();
        return;
      }
      await this.guestAccountService.completeGuestSetup(type);
      this.modalController.dismiss({ guestType: type });
    } catch (err) {
      this.demoAccountService.cancelEntrance();
      if (isConnectionError(err)) {
        this.util.showErrorWithRetry(CONNECTION_ERROR_MESSAGE, () => this.selectAndConfirmGuestType(type));
      } else {
        this.util.showError(type === 'rental' ? DEMO_UNAVAILABLE_MESSAGE : 'Something went wrong. Please try again.');
      }
      console.error('Failed to create guest account:', err);
    } finally {
      this.isLoading = false;
      this.loadingGuestType = null;
    }
  }

  goToSignIn() {
    this.step = 'auth';
    this.mode = 'signin';
  }

  goBackToGuestType() {
    this.step = 'guest-type';
    this.errorMessage = '';
    this.submitted = false;
  }

  async completeSignUp() {
    this.isLoading = true;
    try {
      const formData = this.signUpForm.value;
      const isDemoSession = this.demoAccountService.isDemoSession();
      const authenticatedWithPlaceHolderAccount = !isDemoSession && await this.storage.retrieve('isGuestAccount');
      if (!isDemoSession && this.selectedAccountType === 'rental') {
        // Rental signups start in the shared demo account; their real account is
        // created later (e.g. when they book) from the stashed signup details
        this.demoAccountService.stashPendingSignup(formData);
        this.demoAccountService.beginEntrance();
        await this.demoAccountService.loginToDemoAccount('rental');
        this.dismiss();
        // Property-manager demo landing page is A/B tested (pm-demo-landing-page).
        const route = await this.pmDemoLanding.resolveLandingRoute();
        this.navCtrl.navigateForward(route);
        this.demoAccountService.revealEntrance();
        return;
      }
      if (isDemoSession) {
        // Leave the shared demo account untouched: log out of it and create the
        // account the user actually signed up with, then continue their flow
        const accountType = this.selectedAccountType || localStorage.getItem('guest_view_type') || 'rental';
        this.demoAccountService.dismissWelcome();
        this.clearLocalStoragePreservingPosthog();
        await this.storage.clear();
        await this.createNewAccount(formData, accountType);
        this.auth.setAccountTypeStorage(accountType);
        this.demoAccountService.clearPendingSignup();
      } else if (authenticatedWithPlaceHolderAccount) {
        const payload = {
          profile: {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email: formData.email
          }
        };
        await this.client.saveClientSettings(payload);

        const guestEmail = this.guestAccountService.generateGuestEmail();
        await this.client.saveClientSettings({profile: {email: guestEmail}});

        const passwordPayload = {
          password: formData.password,
          confirm_password: formData.password,
          current_password: 'GuestPass123!'
        };
        await this.auth.changePassword(passwordPayload);
        await this.client.saveClientSettings(payload);
      } else {
        await this.createNewAccount(formData);
      }
      localStorage.setItem('email', formData.email);
      localStorage.setItem('firstName', formData.firstName);
      localStorage.setItem('lastName', formData.lastName);
      await this.storage.save('isGuestAccount', false);
      await this.storage.delete('me');
      const account = await this.me.load(true);
      if (account?.customer_account?.id && account?.customer_account?.email) {
        await this.posthogService.identify(
          account.customer_account.id,
          account.customer_account.email,
          { account_type: account.customer_account.account_type }
        );
        await this.posthogService.reloadFeatureFlags();
        await this.bookService.cacheBookingViewV2Variant();
      }
      this.auth.authenticated.set(true);
      this.events.publish('auth:complete');
      this.dismiss();
      this.util.showSuccess('Account created successfully!');
    } catch (err: any) {
      this.isLoading = false;
      this.demoAccountService.cancelEntrance();
      const errorMessage = err?.message || err?.error?.message;
      this.errorMessage = isConnectionError(err)
        ? CONNECTION_ERROR_MESSAGE
        : errorMessage || 'Failed to create account. Please try again.';
    }
  }

  private async createNewAccount(formData: any, accountTypeOverride?: string) {
    await this.auth.login({}, 'signUp', null);
    const accountType = accountTypeOverride || this.selectedAccountType;
    const customersPayload = {
      customer: {
        booked_via: 'Self Booking',
        email: formData.email,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password,
        account_type: accountType
      }
    };
    const response: any = await this.client.getCustomers(customersPayload);
    localStorage.setItem('customer_id', response?.id);
    if (accountType === 'rental') {
      // New rental account → gate booking until a setup call is scheduled
      // (see DemoBlockService.shouldGateNewRental).
      localStorage.setItem('justCreatedAccount', 'true');
    }
    await this.auth.login(
      { username: formData.email, password: formData.password },
      'signUpPassword',
      null
    );
    this.auth.setSession();
  }

  goBackToAuth() {
    this.step = 'auth';
    this.selectedAccountType = null;
  }

  private async loadAccountAndRedirect(preloadedAccount?: any) {
    try {
      const account = preloadedAccount || await this.me.load(true);
      if (!account?.customer_account?.account_type) {
        console.error('loadAccountAndRedirect: Missing customer_account data');
        throw new Error('Invalid account data');
      }
      this.auth.setAccountTypeStorage(account.customer_account.account_type);
      this.auth.setSession();
      await this.addressesQueries.getAddresses();
      await this.posthogService.identify(
        account.customer_account.id,
        account.customer_account.email,
        { account_type: account.customer_account.account_type }
      );
      await this.posthogService.reloadFeatureFlags();
      await this.bookService.cacheBookingViewV2Variant();
      this.auth.authenticated.set(true);
      this.events.publish('auth:complete');
      this.dismiss();
      if (this.afterLoginRedirect.pendingBookingId || this.afterLoginRedirect.pendingPastJobId) {
        this.openPendingJobLink();
      } else {
        // /login and /create-account only open this popup over the dashboard; once signed
        // in, the address becomes the dashboard's own.
        const path = this.router.url.split('?')[0];
        if (path === '/login' || path === '/create-account') {
          await this.navCtrl.navigateRoot(propertyDashboardPath());
        }
      }
    } catch (err) {
      console.error('loadAccountAndRedirect failed:', err);
      this.isLoading = false;
      this.errorMessage = 'Failed to load account. Please try again.';
    }
  }

  private openPendingJobLink() {
    const { pendingBookingId, pendingPastJobId } = this.afterLoginRedirect;
    this.afterLoginRedirect.pendingBookingId = null;
    this.afterLoginRedirect.pendingPastJobId = null;
    if (pendingBookingId) {
      this.navCtrl.navigateRoot(`booking/${pendingBookingId}`);
    } else if (pendingPastJobId) {
      this.navCtrl.navigateRoot(`past-job/${pendingPastJobId}`);
    }
  }

  forgotPassword() {
    this.step = 'reset-password';
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
    this.resetPasswordForm.reset();
  }

  forgotUsername() {
    this.step = 'forgot-username';
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
    this.forgotUsernameForm.reset();
  }

  goBackToSignIn() {
    this.step = 'auth';
    this.mode = 'signin';
    this.codeSent = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
  }

  loginWithPhone() {
    this.step = 'phone-login';
    this.codeSent = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
    this.phoneLoginForm.reset();
  }

  async onSendPhoneCode() {
    this.submitted = true;
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.phoneLoginForm.controls.phone.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const phone = this.phoneLoginForm.value.phone;
      // Backend always returns success (reveals nothing about matching accounts),
      // so any resolve advances to the code step.
      await this.auth.sendPhoneLoginCode(phone);
      this.codeSent = true;
      this.submitted = false;
      // Neutral, enumeration-safe copy: never claim a code was sent (the number
      // may not match any account), so we don't strand people with a false promise.
      this.successMessage = 'If this number belongs to an account, you\'ll receive a code by text.';
    } catch (_err: any) {
      this.errorMessage = 'Could not send code. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  async onVerifyPhoneCode() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.phoneLoginForm.controls.code.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const wasGuestAccount = await this.storage.retrieve('isGuestAccount');
      const credentials = {
        phone: this.phoneLoginForm.value.phone,
        otp_code: this.phoneLoginForm.value.code
      };
      await this.auth.login(credentials, 'login', null);
      await this.completeCustomerLogin(wasGuestAccount);
    } catch (err: any) {
      this.isLoading = false;
      if (isConnectionError(err)) {
        this.errorMessage = CONNECTION_ERROR_MESSAGE;
      } else if (err?.err?.has_homekeeper_account) {
        this.errorMessage = 'This account is for service providers. Please use the TIDY for Pros app.';
      } else {
        this.errorMessage = 'Invalid or expired code. Please try again.';
      }
    }
  }

  useDifferentPhoneNumber() {
    this.codeSent = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.submitted = false;
    this.phoneLoginForm.controls.code.reset();
  }

  onPhoneLoginBack() {
    if (this.codeSent) {
      this.useDifferentPhoneNumber();
    } else {
      this.goBackToSignIn();
    }
  }

  async onResetPassword() {
    this.submitted = true;
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.resetPasswordForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const requestData = {
        email: this.resetPasswordForm.value.email
      };
      await this.auth.recoverPassword(requestData);
      this.isLoading = false;
      this.successMessage = 'Password reset instructions have been sent to your email.';
      setTimeout(() => {
        this.goBackToSignIn();
      }, 3000);
    } catch (err: any) {
      this.isLoading = false;
      if (err?.status === 404) {
        this.errorMessage = 'No account found with this email address.';
      } else {
        this.errorMessage = 'Failed to send reset email. Please try again.';
      }
    }
  }

  async onForgotUsername() {
    this.submitted = true;
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.forgotUsernameForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const params = {
        phone: this.forgotUsernameForm.value.phone,
        address: {
          address1: this.forgotUsernameForm.value.address1,
          zipcode: this.forgotUsernameForm.value.zipcode
        }
      };
      const response: any = await this.recoverAccountService.recoverUserName(params);
      this.isLoading = false;
      this.successMessage = response?.email ? `Your username is: ${response.email}` : 'Username recovery information has been sent.';
      setTimeout(() => {
        this.goBackToSignIn();
      }, 5000);
    } catch (err: any) {
      this.isLoading = false;
      if (err?.status === 404) {
        this.errorMessage = 'No account found with the provided information.';
      } else {
        this.errorMessage = 'Failed to recover username. Please try again.';
      }
    }
  }

  async onConvertAccount() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.convertForm.valid) {
      return;
    }
    this.isLoading = true;
    try {
      const formData = this.convertForm.value;
      const customerId = localStorage.getItem('customer_id');
      const updateData = {
        customer: {
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName
        }
      };
      await this.client.updateCustomer(updateData, customerId);
      localStorage.setItem('email', formData.email);
      localStorage.setItem('firstName', formData.firstName);
      localStorage.setItem('lastName', formData.lastName);
      await this.posthogService.setUserProperties({ email: formData.email });
      this.events.publish('updateMenu');
      this.dismiss();
      this.util.showSuccess('Account saved successfully!');
    } catch (err: any) {
      this.isLoading = false;
      this.errorMessage = err?.error?.message || 'Failed to save account. Please try again.';
    }
  }

  switchToSignIn() {
    this.step = 'auth';
    this.mode = 'signin';
    this.submitted = false;
    this.errorMessage = '';
  }

  openUrl(url: string) {
    this.util.openUrl(url);
  }

  private clearLocalStoragePreservingPosthog(): void {
    const preservedKeys: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('ph_') || key?.startsWith('posthog_variant_') || key?.startsWith('tidy_theme')) {
        preservedKeys.push([key, localStorage.getItem(key)!]);
      }
    }
    localStorage.clear();
    preservedKeys.forEach(([k, v]) => localStorage.setItem(k, v));
  }

}
